'use client';
import {AppRouterCacheProvider} from '@mui/material-nextjs/v15-appRouter';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import {ThemeProvider} from '@mui/material/styles';
import type {BrandTokens} from '@olympus-frontend/tenant-config';
import {useMemo, type ReactNode} from 'react';
import {prefixer} from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import {createOlympusTheme} from './create-olympus-theme';

interface OlympusThemeProviderProps {
  brand: BrandTokens;
  direction?: 'ltr' | 'rtl';
  children: ReactNode;
}

export function OlympusThemeProvider({brand, direction = 'ltr', children}: OlympusThemeProviderProps) {
  const theme = useMemo(() => createOlympusTheme(brand, direction), [brand, direction]);
  // Overriding stylisPlugins replaces emotion's defaults, so prefixer must be
  // re-added explicitly alongside the RTL plugin (per MUI's RTL guide).
  const cacheOptions = useMemo(
    () => (direction === 'rtl' ? {key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin]} : {key: 'mui'}),
    [direction],
  );

  return (
    <AppRouterCacheProvider options={cacheOptions}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles
          styles={theme => ({
            '::placeholder': {color: theme.palette.colors.text.neutral.placeholder, opacity: 1},
            // FontAwesome 7 made icons fixed-width (`--fa-width: 1.25em`), which pads every
            // glyph narrower than 1.25:1 — 59 of the 63 in the catalog. Restore v6 behaviour
            // and take the width from the glyph itself; opt individual icons back in where
            // aligned columns actually want it.
            '.svg-inline--fa': {'--fa-width': 'auto'},
          })}
        />
        {children}
      </ThemeProvider>
    </AppRouterCacheProvider>
  );
}
